import { Building2, ChevronsUpDown, Check, Plus, User } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'

import {
  useCurrentWorkspace,
} from '@/api/workspace-context-provider'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ROLE_LABELS } from '@/api/workspace-permissions'
import type { WorkspaceRead } from '@/api/queries'

interface WorkspaceSwitcherProps {
  /**
   * Fires after a successful switch — used by the AppShell mobile sheet
   * to close itself once the user navigates.
   */
  onSwitch?: () => void
  /** Visual variant. `sidebar` uses sidebar-tuned colors; `inline` is neutral. */
  variant?: 'sidebar' | 'inline'
}

/**
 * Workspace switcher dropdown — replaces the previous "first workspace
 * name" link with a real selector. Surfaces every workspace the user
 * belongs to, marks the active one, and exposes a "manage workspaces"
 * shortcut for the full CRUD page.
 *
 * States covered:
 *  - **Loading**: skeleton chip (mirrors the height of the loaded chip
 *    so the sidebar doesn't reflow).
 *  - **Error**: muted "Workspaces unavailable" placeholder + a link to
 *    the workspaces page where the same error renders inline with more
 *    context.
 *  - **Empty**: "No workspaces" + a "Create workspace" CTA that routes
 *    to /workspaces.
 *  - **Loaded**: the active workspace name + role badge; the dropdown
 *    lists every workspace grouped Personal-first.
 */
export function WorkspaceSwitcher({
  onSwitch,
  variant = 'sidebar',
}: WorkspaceSwitcherProps) {
  const {
    workspaces,
    currentWorkspace,
    isLoading,
    error,
    setCurrentWorkspace,
  } = useCurrentWorkspace()
  const navigate = useNavigate()

  const triggerBase = cn(
    'group inline-flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm',
    'transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/30',
    variant === 'sidebar'
      ? 'border border-sidebar-border/60 bg-sidebar-accent/30 hover:bg-sidebar-accent/60 text-sidebar-foreground'
      : 'border border-border/60 bg-card hover:bg-accent text-foreground',
  )

  if (isLoading && !currentWorkspace) {
    return (
      <div className="px-3" data-testid="workspace-switcher-loading">
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
    )
  }

  if (error && workspaces.length === 0) {
    return (
      <div className="px-3" data-testid="workspace-switcher-error">
        <button
          type="button"
          onClick={() => navigate({ to: '/workspaces' }).then(() => onSwitch?.())}
          className={cn(triggerBase, 'text-muted-foreground italic')}
        >
          <Building2 className="size-4 shrink-0 opacity-60" />
          <span className="truncate">Workspaces unavailable</span>
        </button>
      </div>
    )
  }

  if (!currentWorkspace) {
    return (
      <div className="px-3" data-testid="workspace-switcher-empty">
        <button
          type="button"
          onClick={() => navigate({ to: '/workspaces' }).then(() => onSwitch?.())}
          className={cn(triggerBase, 'text-muted-foreground')}
        >
          <Plus className="size-4 shrink-0" />
          <span className="truncate">Create workspace</span>
        </button>
      </div>
    )
  }

  // Sort: personal first, then alphabetical so the dropdown is stable.
  const ordered = [...workspaces].sort((a, b) => {
    if (a.is_personal && !b.is_personal) return -1
    if (!a.is_personal && b.is_personal) return 1
    return a.name.localeCompare(b.name)
  })

  const handlePick = (ws: WorkspaceRead) => {
    setCurrentWorkspace(ws.uuid)
    onSwitch?.()
  }

  return (
    <div className="px-3" data-testid="workspace-switcher">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={triggerBase}
            data-testid="workspace-switcher-trigger"
            aria-label="Switch workspace"
          >
            <span
              aria-hidden
              className={cn(
                'inline-flex size-6 shrink-0 items-center justify-center rounded-md',
                variant === 'sidebar'
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'bg-primary-soft text-primary-soft-foreground',
              )}
            >
              {currentWorkspace.is_personal ? (
                <User className="size-3.5" />
              ) : (
                <Building2 className="size-3.5" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {currentWorkspace.name}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {ROLE_LABELS[currentWorkspace.role]}
                {currentWorkspace.member_count > 1 &&
                  ` · ${currentWorkspace.member_count} members`}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-64"
          data-testid="workspace-switcher-menu"
        >
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Workspaces
          </DropdownMenuLabel>
          {ordered.map((ws) => {
            const active = ws.uuid === currentWorkspace.uuid
            return (
              <DropdownMenuItem
                key={ws.uuid}
                data-testid="workspace-switcher-option"
                data-uuid={ws.uuid}
                data-active={active ? 'true' : undefined}
                onSelect={(e) => {
                  e.preventDefault()
                  handlePick(ws)
                }}
                className="flex items-center justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {ws.is_personal ? (
                      <User className="size-3.5 shrink-0 opacity-70" />
                    ) : (
                      <Building2 className="size-3.5 shrink-0 opacity-70" />
                    )}
                    <span className="truncate text-sm">{ws.name}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 pl-5">
                    <Badge tone="outline" size="xs">
                      {ROLE_LABELS[ws.role]}
                    </Badge>
                    {ws.is_personal && (
                      <Badge tone="neutral" size="xs">
                        Personal
                      </Badge>
                    )}
                  </div>
                </div>
                {active && (
                  <Check className="size-4 shrink-0 text-primary" aria-label="Active" />
                )}
              </DropdownMenuItem>
            )
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="workspace-switcher-manage"
            onSelect={(e) => {
              e.preventDefault()
              navigate({ to: '/workspaces' }).then(() => onSwitch?.())
            }}
          >
            <Building2 />
            Manage workspaces
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
